import classNames from "classnames";
import React, { useMemo } from "react";
import { IconButton } from "../button";
import { IconCancel } from "../icons";
import { Text } from "../text";
import { CSS_CLASSES } from "./tag-css-classes";
import { TagIcon } from "./tag-icon";
import { TagProps } from "./tag.types";

/**
 * `Tag` for categorizing or markup.
 */
export const Tag = ({
  as: RootElementTag = "div",
  removable = false,
  onRemove,
  color = "primary",
  inverted = false,
  size = "large",
  disabled = false,
  startIcon,
  endIcon,
  iconColor = "currentcolor",
  className,
  iconClassName,
  textClassName,
  children,
  ...restProps
}: TagProps) => {
  const rootClasses = useMemo(() => {
    return classNames(
      CSS_CLASSES.TAG_ROOT,
      {
        [CSS_CLASSES.TAG_ROOT_PRIMARY]: color === "primary",
        [CSS_CLASSES.TAG_ROOT_SECONDARY]: color === "secondary",
        [CSS_CLASSES.TAG_ROOT_INTERACTIVE]: color === "interactive",
        [CSS_CLASSES.TAG_ROOT_ACCENT]: color === "accent",
        [CSS_CLASSES.TAG_ROOT_ERROR]: color === "error",
        [CSS_CLASSES.TAG_ROOT_WARNING]: color === "warning",
        [CSS_CLASSES.TAG_ROOT_SUCCESS]: color === "success",
        [CSS_CLASSES.TAG_ROOT_GRAY]: color === "gray",

        [CSS_CLASSES.TAG_ROOT_INVERTED]: inverted,

        [CSS_CLASSES.TAG_ROOT_LARGE]: size === "large",
        [CSS_CLASSES.TAG_ROOT_SMALL]: size === "small",

        [CSS_CLASSES.TAG_ROOT_DISABLED]: !!disabled,
      },
      className
    );
  }, [color, inverted, size, disabled, className]);

  const textClasses = useMemo(() => {
    return classNames(
      CSS_CLASSES.TAG_TEXT,
      {
        [CSS_CLASSES.TAG_TEXT_SMALL]: size === "small",
        [CSS_CLASSES.TAG_TEXT_LARGE]: size === "large",
      },
      textClassName
    );
  }, [size, textClassName]);

  const buttonClasses = useMemo(() => classNames(CSS_CLASSES.TAG_BUTTON), []);
  const buttonIconSize = useMemo(() => (size === "large" ? 24 : 14), [size]);

  return (
    <RootElementTag
      className={rootClasses}
      title={typeof children === "string" ? children : undefined}
      {...(restProps as any)}
    >
      {removable && !disabled ? (
        <IconButton
          ripple={false}
          tabIndex={-1}
          size="small"
          icon={<IconCancel className={CSS_CLASSES.TAG_BUTTON_ICON} size={buttonIconSize} />}
          label="remove"
          className={buttonClasses}
          variant="ghost"
          color={inverted ? "white" : color}
          onClick={onRemove}
        />
      ) : (
        <TagIcon icon={startIcon} color={iconColor} position="start" className={iconClassName} />
      )}
      <Text color="currentcolor" className={textClasses}>
        {children}
      </Text>
      <TagIcon icon={endIcon} color={iconColor} position="end" className={iconClassName} />
    </RootElementTag>
  );
};
